// 치과 「환자 정보 처리위탁」 부속 확인 2건 한 곳 — 가입 필수 동의·로그인 후 1회 모달·설정 화면 공용 (2026-10-08 대표 "권고대로")
// 근거: docs/법무검토_처리위탁_가입필수동의_2026-10-08.md §2 ⑤⑥ · §3 ☆ A·B·C · P1-4
//   문서 1 = 데스크 접수 처리위탁 확인(desk-clinic-1.2, utils/deskReception.js DESK_CLINIC_CONSENT)
//   문서 2 = 환자 앱 등록 처리위탁 확인(pa-clinic-1.2, config/careLegal.js CARE_PA_CLINIC)
// 원문·판·해시는 각 원본 상수가 정본 — 여기서 다시 적지 않는다. 화면에는 바이트 그대로(lines.join('\n')) pre-wrap.
// 웹 원문 해시가 상수 textHash 와 다르면 그 문서는 가입 화면에서 생략(⑥ 대표 결정: 생략 후 로그인 모달)하고 콘솔에 판만 남긴다.
// ☆ 표시 = 이번에 새로 쓴 문구(해시 대상 아님 — 변호사 검토 대상).
import { useCallback, useEffect, useState } from 'react';
import { DESK_CLINIC_CONSENT, deskClinicConsentFor, deskClinicConsentText, receptionDeskError } from './deskReception';
import { CARE_PA_CLINIC, isCareTextReady, paClinicText, verifyCareText } from '../config/careLegal';
import { getDeskClinicConsent, acceptDeskClinicConsent } from '../services/receptionApi';
import { getSignupClinicDelegationVersions } from '../services/clinicSignupDelegationApi';
import { getPatientAppClinicConsentShared, invalidatePatientAppClinicConsent, acceptPatientAppClinicConsent, careClinicError } from '../services/careAppApi';

// 문서 표 — 순서 = 화면 순서(문서 1, 문서 2)
export const CLINIC_DELEGATION_DOCS = [
    {
        key: 'desk',
        title: '데스크 접수 처리위탁 확인',
        version: DESK_CLINIC_CONSENT.version,
        textHash: DESK_CLINIC_CONSENT.textHash,
        text: deskClinicConsentText(DESK_CLINIC_CONSENT),
        ready: true,
    },
    {
        key: 'pa',
        title: '환자 앱 등록 처리위탁 확인',
        version: CARE_PA_CLINIC.version,
        textHash: CARE_PA_CLINIC.textHash,
        text: paClinicText(CARE_PA_CLINIC),
        ready: isCareTextReady(CARE_PA_CLINIC),
    },
];
export const delegationDocOf = (key) => CLINIC_DELEGATION_DOCS.find(d => d.key === key) || null;

// ── ☆ 문구 (법무검토 §3) ──
// ☆ A — 가입 화면(치과만)
export const SIGNUP_DELEGATION_TITLE = '[필수] 환자 정보 처리위탁 확인 (치과 회원)';
// 문서가 1건만 표시될 때(⑥ 생략)는 "두 문서"를 "문서"로만 바꾼다
export const signupDelegationIntro = (count) => `우리 치과가 데스크 접수와 환자 앱 등록 기능을 쓸 때 환자 정보의 처리를 덴탈클러스터에 맡기는 약정입니다. 아래 ${count === 2 ? '두 문서' : '문서'} 전문을 읽어 주세요. 가입 승인 후 이 기능을 쓰기 전까지 덴탈클러스터가 처리하는 환자 정보는 없습니다.`;
// ☆ A 보조문(철회 방법 1줄). 데스크 철회 기능은 아직 없음 — 생기면 "각 확인은 따로 철회할 수 있으며"로(법무검토 §3 A 주석)
export function delegationFootnote(keys) {
    const parts = [];
    if (keys.includes('pa')) parts.push('환자 앱 등록 확인은 가입 후 대표 계정으로 언제든 철회할 수 있으며, 철회해도 회원 자격과 다른 기능 이용에는 영향이 없습니다.');
    if (keys.includes('desk')) parts.push('데스크 접수는 쓰지 않으면 맡겨지는 환자 정보가 없습니다.');
    return parts.join(' ');
}
// 원문 마지막 줄 ☐ 안내(10-04 카드와 같은 문장, 여러 문서용으로 "각 글")
export const BOX_MARK_HINT = '각 글 마지막 줄의 네모(☐)는 문안의 일부입니다. 확인은 아래 칸을 눌러 체크해 주세요.';
/** ☆ A 체크 문구 — 남은 문서 이름·판만 넣는다(B: "A와 같되 남은 문서만") */
export function delegationCheckLabel(docs) {
    const names = docs.map(d => `「${d.title}」(${d.version})`);
    const joined = names.length === 2 ? `${names[0]}과 ${names[1]}` : names.join('');
    return `위 ${joined}의 내용을 확인했고, 본인은 우리 치과의 대표자이거나 대표자에게서 이 확인 권한을 받았습니다.`;
}
// ☆ B — 기존 치과 1회 모달(대표)
export const MODAL_TITLE = '환자 정보 처리위탁 확인 — 한 곳에서 확인하기';
export const MODAL_BODY = '지금까지 데스크 접수와 환자 앱 등록은 각 화면에서 따로 확인했습니다. 이제 여기서 한 번에 확인할 수 있습니다. 아직 확인하지 않았거나 내용이 바뀐 문서만 보여 드립니다. 확인하지 않아도 의뢰서·명세서 등 다른 기능은 그대로 쓸 수 있고, 데스크 접수·환자 앱 등록만 열리지 않습니다.';
export const MODAL_LATER_NOTE = '설정 › 환자 정보 처리위탁에서 언제든 확인할 수 있습니다.';
export const confirmedLine = (it) => `이미 확인함 — ${it.title} (${it.confirmedVersion || it.version}${it.atText ? `, ${it.atText}` : ''})`;
// ☆ C — 직원 계정
export const STAFF_TEXT = '치과 대표 계정만 확인할 수 있습니다. 대표가 확인하기 전까지 데스크 접수·환자 앱 등록은 열리지 않으며, 다른 기능은 그대로 쓸 수 있습니다.';

export function fmtYmd(ms) {
    if (!Number.isFinite(Number(ms)) || ms == null) return '';
    const k = new Date(Number(ms) + 9 * 3600 * 1000);
    return `${k.getUTCFullYear()}.${k.getUTCMonth() + 1}.${k.getUTCDate()}`;
}

/**
 * 웹 원문 자체 해시 대조 — 정본과 바이트가 같은 문서만 돌려준다(⑥). 대조 불가(crypto.subtle 없음)도 생략.
 * @returns {Promise<Array>} CLINIC_DELEGATION_DOCS 중 통과한 것
 */
export async function verifiedDelegationDocs() {
    const out = [];
    for (const d of CLINIC_DELEGATION_DOCS) {
        if (!d.ready) { console.error(`[처리위탁 확인] ${d.version} 원문 미확정 — 가입 화면에서 생략`); continue; }
        // verifyCareText 는 status 'final' 묶음만 받는다 — 데스크 원문도 같은 규칙(정본 §1 정규화·sha256)으로 대조
        const ok = await verifyCareText({ status: 'final', version: d.version, textHash: d.textHash, lines: [] }, d.text);
        if (ok) out.push(d);
        else console.error(`[처리위탁 확인] ${d.version} 웹 원문 해시 불일치 — 가입 화면에서 생략, 로그인 후 모달로`);
    }
    return out;
}

/**
 * 가입 화면용 — ① 웹 원문 자체 해시 대조 ② 서버 현재 판·해시(getSignupClinicDelegationVersions, 공개)와 같은 문서만.
 * 둘 중 하나라도 어긋나거나 서버 조회가 실패하면 그 문서는 생략(⑥ — 승인 후 로그인 모달로). 대조 끝나기 전 null.
 */
export function useSignupDelegationDocs(enabled) {
    const [docs, setDocs] = useState(null);
    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;
        (async () => {
            let server = null;
            try {
                server = await getSignupClinicDelegationVersions();
            } catch (e) {
                console.error('[처리위탁 확인] 서버 판 조회 실패 — 가입 화면에서 생략, 로그인 후 모달로:', e?.code || e?.message || e);
            }
            let list = [];
            try { list = await verifiedDelegationDocs(); }
            catch (e) { console.error('[처리위탁 확인] 원문 대조 실패 — 가입 화면에서 생략:', e?.message || e); }
            const out = list.filter(d => {
                const sv = server && server[d.key];
                const same = !!sv && sv.version === d.version && String(sv.textHash).toLowerCase() === String(d.textHash).toLowerCase();
                if (server && !same) console.error(`[처리위탁 확인] ${d.version} 서버 현재 판(${sv?.version || '없음'})과 다름 — 가입 화면에서 생략, 로그인 후 모달로`);
                return same;
            });
            if (alive) setDocs(out);
        })();
        return () => { alive = false; };
    }, [enabled]);
    return docs;
}

// ── 로그인 후(모달·설정) 서버 상태 ──
// 항목: { key, title, version(서버 현재 판), textHash, text, available(웹 원문=서버 현재 판·해시 + 웹 해시 통과), err,
//         confirmed, isCurrent, done(현재 판 확인 완료), confirmedVersion, at, atText, canConfirm, revoked, purgeAt }
// 판정 불가 = done:false·available:false (fail-closed). 조회 실패는 각 API 오류 함수가 콘솔에 남긴다.
async function loadDesk(webOk) {
    const base = delegationDocOf('desk');
    try {
        const d = (await getDeskClinicConsent()) || {};
        const web = d.current ? deskClinicConsentFor(d.current, d.currentTextHash) : null;
        const confirmed = d.confirmed === true;
        const isCurrent = d.isCurrent === true;
        return {
            ...base,
            ...(web ? { version: web.version, textHash: web.textHash, text: deskClinicConsentText(web) } : {}),
            available: !!web && webOk,
            err: '',
            confirmed, isCurrent, done: confirmed && isCurrent,
            confirmedVersion: typeof d.version === 'string' ? d.version : '',
            at: d.at ?? null, atText: fmtYmd(d.at),
            canConfirm: d.canConfirm === true,
            revoked: false, purgeAt: null,
        };
    } catch (e) {
        console.error('[처리위탁 확인] 데스크 접수 확인 상태 조회 실패:', e?.code || e?.message || e, e?.details?.code || '');
        return { ...base, available: false, err: receptionDeskError(e, '처리위탁 확인 상태를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'), confirmed: false, isCurrent: false, done: false, canConfirm: false };
    }
}

async function loadPa(webOk, force) {
    const base = delegationDocOf('pa');
    try {
        if (force) invalidatePatientAppClinicConsent();
        const d = await getPatientAppClinicConsentShared({ force: !!force });
        const same = d.ready !== false && d.current === CARE_PA_CLINIC.version
            && String(d.currentTextHash || '').toLowerCase() === String(CARE_PA_CLINIC.textHash).toLowerCase();
        return {
            ...base,
            available: same && webOk,
            err: d.ready === false ? '확인 문서가 아직 준비되지 않았습니다.' : '',
            confirmed: d.confirmed, isCurrent: d.isCurrent, done: d.confirmed && d.isCurrent,
            confirmedVersion: d.version || '',
            at: d.at, atText: fmtYmd(d.at),
            canConfirm: d.canConfirm,
            revoked: d.revoked, purgeAt: d.purgeAt,
        };
    } catch (e) {
        return { ...base, available: false, err: careClinicError(e, '처리위탁 확인 상태를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'), confirmed: false, isCurrent: false, done: false, canConfirm: false };
    }
}

export async function loadClinicDelegationStatus({ force = false } = {}) {
    const verified = await verifiedDelegationDocs();
    const ok = (k) => verified.some(d => d.key === k);
    return Promise.all([loadDesk(ok('desk')), loadPa(ok('pa'), force)]);
}

/** 상태 훅 — enabled false 면 부르지 않는다 */
export function useClinicDelegationStatus(enabled = true) {
    const [st, setSt] = useState({ loading: !!enabled, items: [] });
    const reload = useCallback(async (force = false) => {
        setSt(s => ({ ...s, loading: true }));
        const items = await loadClinicDelegationStatus({ force });
        setSt({ loading: false, items });
        return items;
    }, []);
    useEffect(() => { if (enabled) reload(false); }, [enabled, reload]);
    return { ...st, reload };
}

/** 확인할 문서(대표가 지금 확인 가능): 현재 판 미확인 + 웹·서버 원문 일치 */
export const pendingItems = (items) => (items || []).filter(it => !it.done && it.available);

/**
 * 남은 문서 확인 기록 — 기존 확인 API(승인된 대표 전용)로 문서별 1건씩. 문서마다 결과를 돌려준다(부분 실패 표시용).
 * @returns {Promise<Array<{key, ok, error}>>}
 */
export async function acceptClinicDelegationDocs(items) {
    const out = [];
    for (const it of items) {
        try {
            const r = it.key === 'desk'
                ? await acceptDeskClinicConsent(it.version, it.textHash)
                : await acceptPatientAppClinicConsent(it.version, it.textHash);
            if (r?.ok !== true) {
                console.error(`[처리위탁 확인] ${it.version} 기록 응답 이상: ok=`, r?.ok);
                out.push({ key: it.key, ok: false, error: `${it.title}: 확인을 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.` });
            } else {
                out.push({ key: it.key, ok: true, error: '' });
            }
        } catch (e) {
            const msg = it.key === 'desk'
                ? (console.error(`[처리위탁 확인] ${it.version} 기록 실패:`, e?.code || e?.message || e, e?.details?.code || ''), receptionDeskError(e, '확인을 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.'))
                : careClinicError(e, '확인을 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.');
            out.push({ key: it.key, ok: false, error: `${it.title}: ${msg}` });
        }
    }
    if (out.some(r => r.key === 'pa' && r.ok)) invalidatePatientAppClinicConsent();
    return out;
}

// ── 로그인 후 1회 모달 판정 기록 (localStorage — 환자 정보 없음, 문서 판 서명만) ──
//   같은 남은 문서 묶음(판 포함)으로는 한 번만 띄운다. 판이 바뀌거나 남은 문서가 바뀌면 다시 띄운다.
//   가입 직후 기록 실패 표식(SIGNUP_FAILED_KEY)이 있으면 서명과 무관하게 띄운다.
export const promptSignature = (items) => items.map(it => `${it.key}:${it.version}`).sort().join('|');
const PROMPTED_KEY = (uid) => `clinicDelegationPrompted_${uid}`;
export const SIGNUP_FAILED_KEY = (uid) => `clinicDelegationSignupFailed_${uid}`;
export function wasPrompted(uid, sig) {
    try {
        if (localStorage.getItem(SIGNUP_FAILED_KEY(uid)) === '1') return false;
        return localStorage.getItem(PROMPTED_KEY(uid)) === sig;
    } catch (e) {
        console.error('[처리위탁 확인] 모달 표시 기록 읽기 실패 — 모달 표시:', e?.message || e);
        return false;
    }
}
export function markPrompted(uid, sig) {
    try {
        localStorage.setItem(PROMPTED_KEY(uid), sig);
        localStorage.removeItem(SIGNUP_FAILED_KEY(uid));
    } catch (e) {
        console.error('[처리위탁 확인] 모달 표시 기록 저장 실패:', e?.message || e);
    }
}
